import {
  type AUTO_CLASSIFICATION_CONFIG_TYPE,
  type CLASSIFICATION_GROUP_BY,
  type FIX_FORM_CONFIG_TYPE,
  type FORM_TYPE,
  type LLM_CONFIG_TYPE,
  type MANUAL_CLASSIFICATION_CONFIG_TYPE,
} from "@/constants/global";
import { type PIPELINE_FIELD_TYPES } from "@/constants/pipeline";

import { type BUILD_TYPE, type EXEC_TYPE } from "../constants/global";
import {
  type FixFormDataEnhancementServer,
  type IDataEnhancementOld,
  type STEP_TYPE_DATA_ENHANCEMENT_OLD,
} from "./fixForm";
import { type SelectOption } from "./global";
import { type LLMSample } from "./llmSample";
import { type Template } from "./template";
import { type UserInShort } from "./user";
import { type WorkflowInShort } from "./workflow";

export type PipelineId = string;

export const PIPELINES_OFFSET_DEFAULT = 0;
export const PIPELINES_LIMIT_DEFAULT = 10;

export enum PipelineStatus {
  DRAFT = "draft",
  EDITING = "editing",
  PROVISIONING = "provisioning",
  PUBLISHING = "publishing",
  PUBLISHED = "published",
  ERROR = "error",
}

export enum PipelineBuildType {
  PREBUILT = "prebuilt",
  VENDOR_CUSTOMIZED = "vendor-customized",
  CLIENT_CUSTOMIZED = "client-customized",
}

export enum LLM_AI_SETTING_MODELS {
  GPT = "gpt",
  CINNAMON_LLM = "cinnamon-llm",
  LOCAL = "local",
}

export type ClassId = string;

export interface LLMPipelineVersion {
  llm_model_type?: LLM_AI_SETTING_MODELS;
  single_fields_llm_model_type?: string;
  table_fields_llm_model_type?: string;

  enable_single_field_extraction?: boolean;
  enable_table_field_extraction?: boolean;
}

export interface PipelineBasicInfo {
  id: PipelineId;
  created_at: string;
  created_by: UserInShort;
  updated_at: string;
  updated_by: UserInShort;
  code_name: string;
  display_name: string;
  description: string;
  meta: Partial<PipelineTemplate> | Record<string, never>;
  templates: Template[];
  version: string;
  status: PipelineStatus;
  build_type: BUILD_TYPE;
  exec_type: EXEC_TYPE;
  form_type: FORM_TYPE;
  workflows: WorkflowInShort[];
  isSelected: boolean;
  target_document: string;
  versions: PipelineVersionBasicInfo[];
  tag: string;
  enable_allow_regular_users_to_register_llm_samples?: boolean;
}

export interface ServerOcrPipeline extends Omit<PipelineBasicInfo, "exec_type" | "targets">, LLMPipelineVersion {
  targets: Record<ClassId, Document>;
  exec_type: EXEC_TYPE.OCR;
}

export interface ServerClassificationPipeline extends Omit<PipelineBasicInfo, "exec_type" | "targets"> {
  detectable_classes?: DetectableClassServer[];
  exec_type: EXEC_TYPE.CLASSIFICATION;
}

export interface OcrPipeline extends Omit<ServerOcrPipeline, "targets" | "exec_type">, LLMPipelineVersion {
  exec_type: EXEC_TYPE.OCR;
  targets: Target[];
}

export interface ClassificationPipeline extends Omit<
  ServerClassificationPipeline,
  "exec_type" | "targets" | "detectable_classes"
> {
  detectable_classes: DetectableClass[];
  exec_type: EXEC_TYPE.CLASSIFICATION;
}

export type AutoClassificationMatchingType = "all" | "any";
export type AutoClassificationTemplateStatus = "processing" | "done" | "error";

export interface AutoClassificationKeywordMatching {
  enabled: boolean;
  keyword_list: string[];
  matching_type: AutoClassificationMatchingType;
}

export interface AutoClassificationTemplateItem {
  template_name: string;
  size: number;
  created_at: string;
  status: AutoClassificationTemplateStatus;
}

export interface AutoClassificationTemplateMatching {
  enabled: boolean;
  template_list: AutoClassificationTemplateItem[];
  matching_threshold: number;
}

export interface AutoClassificationClassConfig {
  class_name: string;
  class_id: string;
  description: string;
  keyword_matching: AutoClassificationKeywordMatching;
  template_matching: AutoClassificationTemplateMatching;
}
export interface AutoClassificationKeywordMatchingConfig {
  id: string;
  enabled: boolean;
  match_mode: AutoClassificationMatchingType;
  keywords: string[];
}

export interface AutoClassificationTemplateMatchingConfig {
  id: string;
  enabled: boolean;
  threshold: string;
}

export interface AutoClassificationClassListItem {
  id: string;
  class_id: string;
  class_name: string;
  description: string;
  priority: number;
  keyword_matching_config: AutoClassificationKeywordMatchingConfig;
  template_matching_config: AutoClassificationTemplateMatchingConfig;
}
export interface CreateAutoClassificationClassPayload {
  class_id: string;
  class_name: string;
  description: string;
}

export type CreateAutoClassificationClassResponse = AutoClassificationClassListItem;

export interface UpdateAutoClassificationClassesPriorityPayload {
  ids: string[];
}

export interface UpdateAutoClassificationClassPayload {
  class_name: string;
  description: string;
  class_id: string;
}

export interface UpdateAutoClassificationKeywordMatchingPayload {
  enabled: boolean;
  match_mode: AutoClassificationMatchingType;
  keywords: string[];
}

export interface AutoClassificationKeywordMatchingServer {
  enabled: boolean;
  match_mode: AutoClassificationMatchingType;
  keywords: string[];
}
export interface AutoClassificationPipeline extends Omit<ClassificationPipeline, "detectable_classes"> {
  class_configs?: AutoClassificationClassConfig[];
}

export interface DetectableClass {
  id: ClassId;
  class_id: ClassId;
  class_name: string;
  description: string;
}

export type DetectableClassServer = Omit<DetectableClass, "id">;

export type PipelineVersionId = string;
export type PipelineVersionTag = string;

export interface PipelineVersionBasicInfo {
  id: PipelineVersionId;
  created_at: string;
  created_by: UserInShort;
  updated_at: string;
  updated_by: UserInShort;
  published_at: string | null;
  published_by: UserInShort | null;
  current: boolean;
  version: string;
  status: PipelineStatus;
  tag: PipelineVersionTag;
  pipeline: PipelineBasicInfo;
  release_note: string | null;
}

export interface PipelineVersionServer extends PipelineVersionBasicInfo, LLMPipelineVersion {
  templates: Template[];
  llm_samples: LLMSample[];
  targets: Record<ClassId, Document>;
  post_processors: FixFormDataEnhancementServer[] | DataEnhancementServer[];
  detectable_classes: DetectableClassServer[] | null;
  class_configs?: AutoClassificationClassConfig[];
}

export interface PipelineVersion extends Omit<
  PipelineVersionServer,
  "targets" | "post_processors" | "detectable_classes"
> {
  targets: Target[];
  post_processors: IDataEnhancement[];
  detectable_classes: DetectableClass[];
  class_configs?: AutoClassificationClassConfig[];

  // TODO: Remove this after all data is migrated to new format (after release)
  isOldDataEnhancementFormat?: boolean;
}

export interface PipelineVersionOld extends Omit<PipelineVersionServer, "targets" | "post_processors"> {
  targets: Target[];
  post_processors: IDataEnhancementOld[];
  detectable_classes: DetectableClass[] | null;
  class_configs?: AutoClassificationClassConfig[];
}

export enum FIELD_TYPES {
  SINGLE_FIELD = "single",
  TABLE_FIELD = "table",
}

export type TargetId = string;

export interface Document {
  id?: TargetId;
  order?: number;
  type?: FIELD_TYPES;
  extraction_type?: PIPELINE_FIELD_TYPES;
  description: string;
  display_name: string;
  copied_from?: TargetId | null;
}

export interface Target extends Document {
  alternative_names?: string[];
  classId: ClassId;
  class?: string;
  confidence?: number;
  isCustomField?: boolean;
  isNewField?: boolean;
}

export interface PipelineTemplate {
  post_processors_classification: IPostPreprocessors;
  pre_processors: Record<string, boolean>;
}

export interface IPostPreprocessors {
  group_by: CLASSIFICATION_GROUP_BY[];
}

export type PipelineType = "prebuilt" | "vendor-customized";

export interface PipelineInShort {
  id: PipelineId;
  code_name: string | null;
  display_name: string;
  description: string | null;
  target_document: string | null;
  build_type: BUILD_TYPE;
  exec_type: EXEC_TYPE;
}

export type PipelineInfo = Pick<PipelineInShort, "id" | "code_name" | "display_name">;
export type DataEnhancementId = string;

export enum STEP_TYPE_DATA_ENHANCEMENT {
  FIND_AND_REPLACE = "find-and-replace",
  TRIM_SPACES = "trim-spaces",
  FORMAT_TEXT = "format-text",
  CONVERT_DATE_FORMAT = "convert_date_format",
  CONVERT_TO_UPPER_CASE = "uppercase",
  CONVERT_TO_LOWER_CASE = "lowercase",
  FIND_AND_REPLACE_REGEX = "find-and-replace-regex",
  CONVERT_BYTE_STRING = "convert-byte-string",
}

export const DATA_ENHANCEMENT_STEPS_WITH_SELECT_ALL_INPUT = [
  STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE,
  STEP_TYPE_DATA_ENHANCEMENT.TRIM_SPACES,
  STEP_TYPE_DATA_ENHANCEMENT.CONVERT_DATE_FORMAT,
  STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_UPPER_CASE,
  STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_LOWER_CASE,
  STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE_REGEX,
  STEP_TYPE_DATA_ENHANCEMENT.CONVERT_BYTE_STRING,
];

export enum FORMAT_TEXT_TYPE_DATA_ENHANCEMENT {
  LITERAL = "literal",
  PLACEHOLDER = "placeholder",
}

export interface IFieldInfo {
  id: string;
  display_name: string;
  label: string;
  type?: FIELD_TYPES;
}

export type FindAndReplaceValue = string[][];
export type TrimSpacesValue = string[];
export type FormatTextValue = (string | SelectOption)[];
export type ConvertDateFormatValue = [SelectOption, string, COMPLETE_DATE_WITHOUT_YEAR | null];
export type FindAndReplaceRegexValue = string[][];
export type ConvertByteStringValue = [CONVERT_BYTE_STRING_MODE | null];
export type DataEnhancementValue =
  | FindAndReplaceValue
  | TrimSpacesValue
  | FormatTextValue
  | ConvertDateFormatValue
  | FindAndReplaceRegexValue
  | ConvertByteStringValue;

export interface IDataEnhancementBase {
  id: DataEnhancementId;
  action: STEP_TYPE_DATA_ENHANCEMENT | STEP_TYPE_DATA_ENHANCEMENT_OLD | null;
  value: DataEnhancementValue;
  input: IFieldInfo[];
  output: IFieldInfo[];
  description: string;

  canExpand?: boolean;
}

export type IDataEnhancement = IDataEnhancementBase &
  (
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE;
        value: FindAndReplaceValue;
      }
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.TRIM_SPACES;
        value: TrimSpacesValue;
      }
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.FORMAT_TEXT;
        value: FormatTextValue;
      }
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.CONVERT_DATE_FORMAT;
        value: ConvertDateFormatValue;
      }
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_UPPER_CASE;
        value: FormatTextValue;
      }
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_LOWER_CASE;
        value: FormatTextValue;
      }
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE_REGEX;
        value: FindAndReplaceRegexValue;
      }
    | {
        action: STEP_TYPE_DATA_ENHANCEMENT.CONVERT_BYTE_STRING;
        value: ConvertByteStringValue;
      }
    | {
        action: null;
      }
  );

export interface ITemplateDataEnhancement {
  type: FORMAT_TEXT_TYPE_DATA_ENHANCEMENT;
  value: string;
}

export enum COMPLETE_DATE_WITHOUT_YEAR {
  BEFORE_UPLOAD_DATE = "before_upload_date",
  AFTER_UPLOAD_DATE = "after_upload_date",
}

export enum CONVERT_BYTE_STRING_MODE {
  SINGLE_TO_DOUBLE = "single-to-double",
  DOUBLE_TO_SINGLE = "double-to-single",
}

export type DataEnhancementServer = Partial<Omit<IDataEnhancement, "input" | "output">> & {
  template?: ITemplateDataEnhancement[];
  find?: string[] | string;
  replace?: string;
  remove_all?: boolean;
  date_format?: string;
  return_original_if_fails?: boolean;
  input: string[];
  output: string[] | string;
  complete_missing_year?: COMPLETE_DATE_WITHOUT_YEAR | null;
  mode?: CONVERT_BYTE_STRING_MODE | null;
};

export type DataEnhancementServerOld = Partial<Omit<IDataEnhancementOld, "input" | "output">> & {
  template?: ITemplateDataEnhancement[];
  find?: string[];
  replace?: string;
  remove_all?: boolean;
  input: string[];
  output: string[];
};

export type ReleaseConfigType =
  | FIX_FORM_CONFIG_TYPE.RELEASES
  | LLM_CONFIG_TYPE.RELEASES
  | MANUAL_CLASSIFICATION_CONFIG_TYPE.RELEASES
  | AUTO_CLASSIFICATION_CONFIG_TYPE.RELEASES;
